import {
  Accordion,
  Avatar,
  Box,
  Button,
  Card,
  Center,
  Grid,
  Group,
  Pagination,
  Stack,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { IconSearch, IconUserQuestion, IconUsers } from '@tabler/icons-react';
import Modal from 'components/ModalPopup';
import React, { useEffect, useState } from 'react';
import AccordionModuleDetail from './AccordionModuleDetail';
import Badge from 'components/Badge';
import { RESPONSES_TYPE_COLOR } from 'constant/responseTypeColor';

interface ModalRespondentResponseProps {
  opened: boolean;
  onClose: () => void;
  respondent: any;
  assessment?: any;
}

interface QuestionData {
  answer: string;
  answerLabel: string;
  notes: string | null;
  respondentType: string;
  score: number;
  sequence: number;
  title: string;
  trAssessmentQuestionID: string;
  type: 'Range' | 'Free-text';
}

export interface ModuleData {
  evidences: any[];
  moduleID: string;
  questions: QuestionData[];
  trAssessmentModuleID: string;
  moduleName?: string;
}

interface ParticipantData {
  modules: ModuleData[];
  participantName: string;
  trAssessmentParticipantID: string;
  trAssessmentParticipantResponseID: string;
}

const ModalRespondentResponse: React.FC<ModalRespondentResponseProps> = ({
  opened,
  onClose,
  respondent,
  assessment
}) => {
  const [participantResponse, setParticipantResponse] = useState<null | string>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [participantResponses, setParticipantResponses] = useState<ParticipantData[]>([]);
  const [isTeamAssessment, setIsTeamAssessment] = useState(false);
  const [teamModules, setTeamModules] = useState<ModuleData[]>([]);

  const itemsPerPage = 10;

  useEffect(() => {
    // External respondents shape
    if (respondent?.isExternal) {
      const typeAssessment = respondent?.typeAssessment || assessment?.typeAssessment;
      if (typeAssessment === 'Team') {
        setIsTeamAssessment(true);
        // For Team external, modules may live at the top-level respondent.modules
        if (Array.isArray(respondent?.modules) && respondent.modules.length > 0) {
          setTeamModules(respondent.modules);
        } else {
          const firstPR = respondent?.participantResponses?.[0];
          setTeamModules(firstPR?.modules || []);
        }
        setParticipantResponses([]);
      } else {
        setIsTeamAssessment(false);
        setTeamModules([]);
        setParticipantResponses(respondent?.participantResponses || []);
      }
    } else if (respondent?.responses && respondent.responses.length > 0) {
      // Internal respondents shape
      const response = respondent.responses[0];
      const typeAssessment = response?.typeAssessment || assessment?.typeAssessment;

      if (typeAssessment === 'Team') {
        setIsTeamAssessment(true);
        setTeamModules(response?.modules || []);
        setParticipantResponses([]);
      } else {
        // Individual assessment - has participantResponses
        setIsTeamAssessment(false);
        setTeamModules([]);
        setParticipantResponses(response?.participantResponses || []);
      }
    }

    return () => {
      setParticipantResponses([]);
      setTeamModules([]);
      setIsTeamAssessment(false);
    };
  }, [respondent, assessment?.typeAssessment]);

  const filteredParticipants = participantResponses.filter((response) =>
    response?.participantName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(filteredParticipants.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedParticipants = filteredParticipants.slice(startIndex, startIndex + itemsPerPage);

  const selectedParticipant = participantResponses.find(
    (response) =>
      response?.trAssessmentParticipantResponseID === participantResponse ||
      response?.trAssessmentParticipantID === participantResponse
  );

  const participantName = selectedParticipant?.participantName || 'No name available';

  const role = () => {
    if (respondent?.isExternal) return 'External';
    if (respondent?.respondentType) return respondent?.respondentType;
    return '-';
  };
  const participantRole = role() === 'Supervisor' ? '' : role();

  const handleCloseModal = () => {
    setCurrentPage(1);
    setSearchQuery('');
    setParticipantResponse(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      close={handleCloseModal}
      withCloseButton
      size='full'
      title={
        <Stack gap={'sm'}>
          <Title order={3}>Detail Responses</Title>
          <Group pos={'sticky'} top={0}>
            <Avatar color='#7B2183' radius='xl' size='md'>
              {respondent?.isExternal ? 'E' : respondent?.name ? respondent?.name.charAt(0) : 'R'}
            </Avatar>
            <Stack gap={0}>
              <Title order={4}>
                {respondent?.isExternal
                  ? 'External Respondent'
                  : respondent?.name || respondent?.binusianID || 'Respondent'}
              </Title>
              <Text size='sm' c='dimmed'>
                {respondent?.isExternal ? 'External' : respondent?.respondentType} •{' '}
                {respondent?.isExternal
                  ? respondent?.externalEmail
                  : respondent?.email || 'No email available'}
              </Text>
            </Stack>
          </Group>
        </Stack>
      }
    >
      <Grid w={'80vw'}>
        {/* Left Panel - Only show for Individual assessments */}
        {!isTeamAssessment && (
          <Grid.Col span={'content'}>
            <Card withBorder>
              <Card.Section p='md' pb='xs'>
                <Group gap={4}>
                  <IconUsers size={14} />
                  <Text c='dark' fw={500} size='md'>
                    Participant List
                  </Text>
                </Group>
                <Text c='dimmed' size='sm'>
                  Select a participant to view details
                </Text>
              </Card.Section>
              <Card.Section p={0}>
                <Box px='md' pb='sm'>
                  <TextInput
                    placeholder='Search participant...'
                    leftSection={<IconSearch size={16} />}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </Box>

                <Box style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }} px='xs'>
                  {participantResponses.length === 0 ? (
                    <Box py='xl' ta='center'>
                      <Text c='dimmed'>No participants found</Text>
                    </Box>
                  ) : (
                    <Stack gap={4} py='sm'>
                      {paginatedParticipants?.map((response: ParticipantData, index: number) => {
                        const responseKey =
                          response?.trAssessmentParticipantResponseID ||
                          response?.trAssessmentParticipantID ||
                          String(index);
                        const isActive = participantResponse === responseKey;

                        return (
                          <Button
                            fullWidth
                            key={responseKey}
                            onClick={() => setParticipantResponse(responseKey)}
                            variant={isActive ? 'filled' : 'subtle'}
                          >
                            {response?.participantName || 'Participant ' + index}
                          </Button>
                        );
                      })}
                    </Stack>
                  )}
                </Box>

                {participantResponses.length > itemsPerPage && (
                  <Card.Section p={'lg'} withBorder>
                    <Pagination
                      total={totalPages}
                      value={currentPage}
                      onChange={(page) => setCurrentPage(page)}
                      size='xs'
                    />
                  </Card.Section>
                )}
              </Card.Section>
            </Card>
          </Grid.Col>
        )}

        {/* Right Panel - Content */}
        <Grid.Col span={isTeamAssessment ? 12 : 'auto'}>
          <Card withBorder>
            {/* Team Assessment - Show modules directly */}
            {isTeamAssessment ? (
              <>
                <Stack gap={2} mb={24}>
                  {participantRole && (
                    <Box>
                      <Badge
                        bg={
                          RESPONSES_TYPE_COLOR[
                            participantRole as keyof typeof RESPONSES_TYPE_COLOR
                          ] || 'gray'
                        }
                      >
                        {participantRole}
                      </Badge>
                    </Box>
                  )}
                  <Text size='md' fw={600}>
                    Team Assessment Response
                  </Text>
                  <Text size='sm' c='dimmed'>
                    Assessment for: Team
                  </Text>
                </Stack>
                <Accordion variant='separated' defaultValue='module-0'>
                  {teamModules?.map((module, index) => (
                    <AccordionModuleDetail
                      key={module.trAssessmentModuleID || index}
                      data={module}
                    />
                  ))}
                </Accordion>
              </>
            ) : (
              <>
                {!participantResponse && (
                  <Center py={'xl'}>
                    <Stack align='center'>
                      <Avatar color='primary' radius='xl' size='xl'>
                        <IconUserQuestion size={48} />
                      </Avatar>
                      <Stack align='center' gap={2}>
                        <Title order={2} c='dark'>
                          No participant selected
                        </Title>
                        <Text ta={'center'} c={'dimmed'} fz={'sm'} maw={400}>
                          To start viewing responses, please select a participant from the
                          participant list to view their detailed responses.
                        </Text>
                      </Stack>
                    </Stack>
                  </Center>
                )}
                {participantResponse && (
                  <>
                    <Stack gap={2} mb={24}>
                      {participantRole && (
                        <Box>
                          <Badge
                            bg={
                              RESPONSES_TYPE_COLOR[
                                participantRole as keyof typeof RESPONSES_TYPE_COLOR
                              ] || 'gray'
                            }
                          >
                            {participantRole}
                          </Badge>
                        </Box>
                      )}
                      <Text size='md' fw={600}>
                        {participantName}
                      </Text>
                    </Stack>
                    <Accordion variant='separated' defaultValue='module-0'>
                      {participantResponses
                        .find(
                          (response) =>
                            response?.trAssessmentParticipantResponseID === participantResponse ||
                            response?.trAssessmentParticipantID === participantResponse
                        )
                        ?.modules?.map((module, index) => (
                          <AccordionModuleDetail
                            key={module.trAssessmentModuleID || index}
                            data={module}
                          />
                        ))}
                    </Accordion>
                  </>
                )}
              </>
            )}
          </Card>
        </Grid.Col>
      </Grid>
    </Modal>
  );
};

export default ModalRespondentResponse;
